import React, { useState, useEffect, useCallback, useRef } from 'react'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import 'bootstrap-daterangepicker/daterangepicker.css'
import '../css/FilterPanelSubs.css'
import '../css/ReactDatepicker.css'
import moment from 'moment'
import 'moment/locale/ru'
import { Loaders } from "./Loaders"
import CustomSelect from "./CustomSelect" // ← добавляем импорт
import {useLocation, useNavigate} from "react-router-dom";

// УСТАНАВЛИВАЕМ MOMENT ГЛОБАЛЬНО ПРЯМО ЗДЕСЬ
if (typeof window !== 'undefined') {
    window.moment = moment;
}

const FilterPanelSubs = () => {
    moment.locale('ru')
    const navigate = useNavigate()
    const routerLocation = useLocation()
    const [searchValue, setSearchValue] = useState('')
    const [subsType, setSubsType] = useState('id')
    const [subsStatus, setSubsStatus] = useState('1')
    const [dateRange, setDateRange] = useState([null, null])
    const [startDate, endDate] = dateRange
    const [searchTimeout, setSearchTimeout] = useState(null)
    const [isInitialized, setIsInitialized] = useState(false)
    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const [picker, setPicker] = useState(null)
    const syncFromUrlUnlockRef = useRef(null)

    // Refs для отслеживания первоначальных значений
    const initialValuesRef = useRef({searchValue: '', subsType: 'id', subsStatus: '1', startDate: null, endDate: null})
    const isSyncingFromUrlRef = useRef(false)

    // Инициализация всех параметров из URL
    useEffect(() => {
        const initializeFromURL = () => {
            if (syncFromUrlUnlockRef.current) {
                clearTimeout(syncFromUrlUnlockRef.current)
            }

            isSyncingFromUrlRef.current = true

            const urlParams = new URLSearchParams(routerLocation.search)

            // Инициализация поиска
            const searchParam = urlParams.get('search')
            const searchTypeParam = urlParams.get('searchType')
            const initialSearchValue = searchParam || ''
            const initialSubsType = searchTypeParam || 'id'

            setSearchValue(initialSearchValue)
            setSubsType(initialSubsType)

            // Инициализация статуса
            const statusParam = urlParams.get('status')
            const initialSubsStatus = statusParam || '1'
            setSubsStatus(initialSubsStatus)

            // Инициализация дат из URL
            const dateRangeParam = urlParams.get('subsDataRange')
            let dateStart = null
            let dateEnd = null

            if (dateRangeParam) {
                const [startStr, endStr] = dateRangeParam.split('-')
                if (startStr && startStr.split('.').length === 3) {
                    const [day, month, year] = startStr.split('.')
                    dateStart = moment(`${year}-${month}-${day}`).toDate()
                }
                if (endStr && endStr.split('.').length === 3) {
                    const [day, month, year] = endStr.split('.')
                    dateEnd = moment(`${year}-${month}-${day}`).toDate()
                }
            }

            setDateRange([dateStart, dateEnd])

            initialValuesRef.current = {
                searchValue: initialSearchValue,
                subsType: initialSubsType,
                subsStatus: initialSubsStatus,
                startDate: dateStart,
                endDate: dateEnd
            }
            setIsInitialized(true)

            syncFromUrlUnlockRef.current = setTimeout(() => {
                isSyncingFromUrlRef.current = false
                syncFromUrlUnlockRef.current = null
            }, 0)
        }

        initializeFromURL()

        return () => {
            if (syncFromUrlUnlockRef.current) {
                clearTimeout(syncFromUrlUnlockRef.current)
                syncFromUrlUnlockRef.current = null
            }
        }
    }, [routerLocation.search])

    // Кастомизация панели кнопок
    useEffect(() => {
        if (isPickerOpen) {
            // Ждем появления панели в DOM
            setTimeout(() => {
                const cancelBtn = document.querySelector('.daterangepicker .cancelBtn');
                const applyBtn = document.querySelector('.daterangepicker .applyBtn');
                const drpButtons = document.querySelector('.daterangepicker .drp-buttons');
                const drpSelected = document.querySelector('.daterangepicker .drp-selected');

                if (cancelBtn && applyBtn && drpButtons) {
                    // Скрываем стандартную кнопку "Отмена"
                    cancelBtn.style.display = 'none';

                    // Создаем кнопку "Сброс" если её ещё нет
                    if (!document.querySelector('.daterangepicker .reset-btn')) {
                        const resetBtn = document.createElement('button');
                        resetBtn.type = 'button';
                        resetBtn.className = 'btn btn-default reset-btn';
                        resetBtn.textContent = 'Сброс';
                        resetBtn.style.marginRight = '5px';

                        resetBtn.onclick = (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleResetDateRange();
                        };

                        // Вставляем перед кнопкой "Применить"
                        drpButtons.insertBefore(resetBtn, applyBtn);
                    }
                }
            }, 100);
        }
    }, [isPickerOpen]);

    // Эффект для скрытия календаря при инициализации
    useEffect(() => {
        if (isInitialized) {
            const hideDatePicker = () => {
                const datePickers = document.querySelectorAll('.daterangepicker');
                datePickers.forEach(picker => {
                    picker.style.display = 'none';
                    picker.style.opacity = '0';
                    picker.style.visibility = 'hidden';
                });

                const interval = setInterval(() => {
                    const pickers = document.querySelectorAll('.daterangepicker');
                    pickers.forEach(picker => {
                        if (!isPickerOpen && picker.style.display !== 'none') {
                            picker.style.display = 'none';
                        }
                    });
                }, 500);

                return interval;
            };

            const intervalId = hideDatePicker();

            return () => {
                if (intervalId) clearInterval(intervalId);
            };
        }
    }, [isInitialized, isPickerOpen]);

    // Обработчик кликов вне календаря
    useEffect(() => {
        const handleClickOutside = (event) => {
            const datePicker = document.querySelector('.daterangepicker');
            const datePickerInput = document.querySelector('.date-picker-wrapper input');

            if (datePicker && isPickerOpen &&
                !datePicker.contains(event.target) &&
                !datePickerInput?.contains(event.target)) {

                setIsPickerOpen(false);
                datePicker.style.display = 'none';
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isPickerOpen]);

    // Настройки для DateRangePicker
    const getOptions = () => {
        const baseOptions = {
            locale: {
                format: 'DD.MM.YYYY',
                separator: ' - ',
                applyLabel: 'Применить',
                cancelLabel: 'Отмена',
                fromLabel: 'От',
                toLabel: 'До',
                customRangeLabel: 'Выбрать',
                daysOfWeek: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
                monthNames: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
                firstDay: 1
            },
            dateLimit: {
                days: 30
            },
            minDate: moment().subtract(5, 'year'),
            maxDate: moment().add(1, 'year'),
            opens: 'left',
            alwaysShowCalendars: true,
            showDropdowns: true,
            autoApply: false,
            showWeekNumbers: false,
            singleDatePicker: false,
            timePicker: false,
            timePicker24Hour: false,
            linkedCalendars: true,
            autoUpdateInput: true,
        };

        if (startDate && endDate) {
            return {
                ...baseOptions,
                startDate: moment(startDate),
                endDate: moment(endDate)
            };
        }

        return {
            ...baseOptions,
            startDate: moment().subtract(29, 'days'),
            endDate: moment()
        };
    };

    const navigateWithFilters = useCallback((preservePage = true, forcePageReset = false) => {
        if (!isInitialized) return

        const params = new URLSearchParams()

        if (preservePage && !forcePageReset) {
            const currentParams = new URLSearchParams(routerLocation.search)
            const pageParam = currentParams.get('page')
            if (pageParam) {
                params.set('page', pageParam)
            }
        }

        if (searchValue) {
            params.set('search', searchValue)
            params.set('searchType', subsType)
        }

        if (subsStatus && subsStatus !== 'random') {
            params.set('status', subsStatus)
        }

        if (startDate && endDate) {
            const startFormatted = moment(startDate).format('DD.MM.YYYY')
            const endFormatted = moment(endDate).format('DD.MM.YYYY')
            params.set('subsDataRange', `${startFormatted}-${endFormatted}`)
        }

        const newURL = `${routerLocation.pathname}${params.toString() ? '?' + params.toString() : ''}`
        const currentURL = `${routerLocation.pathname}${routerLocation.search}`

        if (currentURL !== newURL) {
            navigate(newURL)
        }
    }, [searchValue, subsType, subsStatus, startDate, endDate, isInitialized, routerLocation.pathname, routerLocation.search, navigate])

    // Функция сброса дат
    const handleResetDateRange = () => {
        if (picker) {
            picker.setStartDate(moment().subtract(29, 'days'))
            picker.setEndDate(moment())
            picker.updateView()
            picker.hide()
        }

        setDateRange([null, null])

        // Закрываем календарь
        setIsPickerOpen(false);

        // Скрываем календарь
        setTimeout(() => {
            const datePickers = document.querySelectorAll('.daterangepicker');
            datePickers.forEach(picker => {
                picker.style.display = 'none';
            });
        }, 100);

        // Обновляем URL
        const params = new URLSearchParams(routerLocation.search)
        params.delete('subsDataRange')

        const newURL = `${routerLocation.pathname}${params.toString() ? '?' + params.toString() : ''}`
        navigate(newURL)
    }

    useEffect(() => {
        if (!isInitialized || isSyncingFromUrlRef.current) return

        const hasStatusChanged = subsStatus !== initialValuesRef.current.subsStatus

        if (hasStatusChanged) {
            navigateWithFilters(true, true) // сохраняем другие фильтры, но сбрасываем страницу
        }
    }, [subsStatus, navigateWithFilters, isInitialized])

    useEffect(() => {
        if (!isInitialized || isSyncingFromUrlRef.current) return

        const hasSearchChanged = searchValue !== initialValuesRef.current.searchValue ||
            subsType !== initialValuesRef.current.subsType

        if (!searchValue && subsType !== initialValuesRef.current.subsType) {
            return
        }

        if (!hasSearchChanged) return

        if (searchTimeout) {
            clearTimeout(searchTimeout)
        }

        const timeout = setTimeout(() => {
            navigateWithFilters(true, true)
        }, 1000)

        setSearchTimeout(timeout)

        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout)
            }
        }
    }, [searchValue, subsType, isInitialized])

    useEffect(() => {
        if (!isInitialized || isSyncingFromUrlRef.current) return

        const hasDateChanged =
            startDate?.getTime() !== initialValuesRef.current.startDate?.getTime() ||
            endDate?.getTime() !== initialValuesRef.current.endDate?.getTime()

        if (!hasDateChanged) return

        if ((startDate && endDate) || (!startDate && !endDate)) {
            navigateWithFilters(true, true)
        }
    }, [startDate, endDate, navigateWithFilters, isInitialized])

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value)
    }

    const handleSubsTypeChange = (value) => {
        setSubsType(value)
    }

    const handleSubsStatusChange = (value) => {
        setSubsStatus(value)
    }

    const handleApply = (event, picker) => {
        setDateRange([picker.startDate.toDate(), picker.endDate.toDate()])
        setIsPickerOpen(false)

        setTimeout(() => {
            const datePickers = document.querySelectorAll('.daterangepicker');
            datePickers.forEach(picker => {
                picker.style.display = 'none';
            });
        }, 100);
    }

    const handleCancel = (event, picker) => {
        setIsPickerOpen(false);
        setTimeout(() => {
            const datePickers = document.querySelectorAll('.daterangepicker');
            datePickers.forEach(picker => {
                picker.style.display = 'none';
            });
        }, 100);
    }

    const handleShow = (_event, currentPicker) => {
        setPicker(currentPicker)
        setIsPickerOpen(true);
        setTimeout(() => {
            const datePickers = document.querySelectorAll('.daterangepicker');
            datePickers.forEach(picker => {
                picker.style.display = 'block';
                picker.style.opacity = '1';
                picker.style.visibility = 'visible';
            });
        }, 10);
    }

    const handleHide = () => {
        setIsPickerOpen(false);
        setTimeout(() => {
            const datePickers = document.querySelectorAll('.daterangepicker');
            datePickers.forEach(picker => {
                picker.style.display = 'none';
            });
        }, 100);
    }

    const getDisplayDate = () => {
        if (startDate && endDate) {
            return `${moment(startDate).format('DD.MM.YYYY')} - ${moment(endDate).format('DD.MM.YYYY')}`
        }
        return ''
    }

    if (!isInitialized) {
        return <div className="loadersComponent"><Loaders/></div>
    }

    return (
        <div className="componentFilterSubs">
            <div className="filter-toolbar-shell">
                <div className="filter-container">
                    <div className="filter-group filter-group--search">
                        <div className="filter-item filter-item--search">
                            <input
                                className="filter-control search-input"
                                placeholder='Поиск'
                                value={searchValue}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <div className="select-wrapper select-wrapper--inline">
                            <div className="label-text">по</div>
                            <CustomSelect
                                value={subsType}
                                options={[
                                    { value: 'id', label: 'ID подписки' },
                                    { value: 'email', label: 'E-mail' },
                                    { value: 'companyName', label: 'Название компании' },
                                    { value: 'companyCode', label: 'ИНН' },
                                    { value: 'agreementNumber', label: 'Номер договора' }
                                ]}
                                onChange={handleSubsTypeChange}
                                className="filter-control"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                    </div>

                    <div className="filter-group filter-group--meta">
                        <div className="select-wrapper">
                            <div className="label-text">статус</div>
                            <CustomSelect
                                value={subsStatus}
                                options={[
                                    { value: '1', label: 'Активные' },
                                    { value: '0', label: 'Любой' },
                                    { value: '2', label: 'Не активные' }
                                ]}
                                onChange={handleSubsStatusChange}
                                className="filter-control"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                        <div className="select-wrapper">
                            <div className="label-text">дата истечения</div>
                            <div className="date-picker-wrapper filter-control">
                                <DateRangePicker
                                    onApply={handleApply}
                                    onCancel={handleCancel}
                                    onShow={handleShow}
                                    onHide={handleHide}
                                    initialSettings={getOptions()}
                                    containerStyles={{ display: 'block' }}
                                >
                                    <input
                                        type="text"
                                        className="date-input"
                                        value={getDisplayDate()}
                                        readOnly
                                        placeholder="Выберите даты"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            setIsPickerOpen(true);
                                            setTimeout(() => {
                                                const datePickers = document.querySelectorAll('.daterangepicker');
                                                datePickers.forEach(picker => {
                                                    picker.style.display = 'block';
                                                });
                                            }, 10);
                                        }}
                                    />
                                </DateRangePicker>
                                {/* Убираем кнопку сброса отсюда - она теперь внутри календаря */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FilterPanelSubs

